import type React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import { ProductDraftsNoticeContent } from './ProductDraftsNoticeContent';
import type { ProductDraftsNoticeProps } from './productDraftsNoticeTypes';

/**
 * The drafts notice as a section of its own (bos-app#226, rulebook §5.2 and
 * §12): it reads the catalog's draft count itself, as Export Excel runs its
 * own export. It shows nothing before the count arrives or while there is no
 * draft, and says so with a retry when the count fails (review of
 * bos-app#229, finding 17). The boundary keeps a render crash here from
 * taking the toolbar with it; its Try again renders the section again, and
 * it resets by itself when the list switches to or from drafts only.
 */
export const ProductDraftsNotice: React.FC<ProductDraftsNoticeProps> = ({
    isDraftsOnly,
    onShowDrafts,
}) => {
    const { t } = useTranslation('pages');

    return (
        <ErrorBoundary
            data-testid="products-drafts-notice-boundary"
            fallbackMessage={t(
                'products.components.productTable.drafts.crashedMessage',
            )}
            id="products-drafts-notice-boundary"
            resetKeys={[isDraftsOnly]}
            retryLabel={t('products.components.productTable.drafts.retry')}
            title={t('products.components.productTable.drafts.crashedTitle')}
        >
            <ProductDraftsNoticeContent
                isDraftsOnly={isDraftsOnly}
                onShowDrafts={onShowDrafts}
            />
        </ErrorBoundary>
    );
};
